import { api } from "../../../../services/rpc/RpcProvider";
import {
  Button,
  ConfirmationModal,
  ModalV2,
  Stack,
  Title,
  useToast
} from "@introist/react-foundation/v2";
import { useCallback, useState } from "react";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import { FormFooter } from "../../components/FormFooter";
import { CodeEditor } from "../../../../components/templates/CodeEditor/CodeEditor";
import { useFeatureFlag } from "../../../../hooks";
import { TestCasesPane } from "../../../../components/TransformBuilder/TestCasesPane";
import { ChatMessage, ChatPane } from "../../../../components/TransformBuilder/ChatPane";
import {
  parseTestCase,
  TestCase,
  TestCaseFormat,
  toTestCase,
  TransformTestResult
} from "../../../../components/TransformBuilder/testcases";

type TransformEditorModalProps = {
  dataSourceId: string;
  mappingEntryId: string;
  defaultValue?: string;
  open: boolean;
  onClose: () => void;
};

const template = `function transform(mapped, raw) {
  // Return only value for the field
  return undefined;
}
`;

const format: TestCaseFormat = { args: ["mapped", "raw"] };

type MappingTestCase = {
  id: string;
  name: string;
  mapped: Record<string, unknown>;
  raw: Record<string, unknown>;
};

const byId = (results: TransformTestResult[]) => Object.fromEntries(results.map(r => [r.id, r]));

export const TransformEditorModal = ({
  dataSourceId,
  mappingEntryId,
  defaultValue,
  open,
  onClose
}: TransformEditorModalProps) => {
  const toast = useToast();
  const { isEnabled: aiEnabled } = useFeatureFlag("ai-transform-builder");

  const [value, setValue] = useState(defaultValue ?? template);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [results, setResults] = useState<Record<string, TransformTestResult>>({});
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [remove, setRemove] = useState(false);

  const update = api.employees.dataSources.mappings.update.useMutation();
  const listMappings = api.useContext().employees.dataSources.mappings.list;
  const runTests = api.employees.dataSources.testTransformCases.useMutation();
  const assistant = api.employees.dataSources.transformAssistant.useMutation();

  const parseTestCases = useCallback(() => {
    try {
      // parseTestCase guarantees mapped and raw are objects for this format
      return testCases.map(t => parseTestCase(format, t) as MappingTestCase);
    } catch (e: any) {
      toast.error(e.message);
      return undefined;
    }
  }, [testCases, toast]);

  const onRunTests = useCallback(async () => {
    const cases = parseTestCases();
    if (!cases) return;

    return runTests
      .mutateAsync({ dataSourceId, transform: value, cases })
      .then(res => setResults(byId(res)))
      .catch(() => toast.error("Failed to run tests"));
  }, [parseTestCases, runTests, dataSourceId, value, toast]);

  const onSendMessage = useCallback(
    async (content: string) => {
      const cases = parseTestCases();
      if (!cases) return;

      const history: ChatMessage[] = [...messages, { role: "user", content }];
      setMessages(history);

      return assistant
        .mutateAsync({
          dataSourceId,
          mappingId: mappingEntryId,
          messages: history.flatMap(m => (m.role === "note" ? [] : [{ ...m, role: m.role }])),
          code: value,
          testCases: cases
        })
        .then(res => {
          const notes: ChatMessage[] = [];
          if (res.code !== undefined && res.code !== value) {
            setValue(res.code);
            notes.push({ role: "note", content: "Updated the code" });
          }
          if (res.newTestCases.length > 0) {
            setTestCases(current => [
              ...current,
              ...res.newTestCases.map(t => toTestCase(format, t))
            ]);
            const count = res.newTestCases.length;
            notes.push({
              role: "note",
              content: `Added ${count} test case${count === 1 ? "" : "s"}`
            });
          }
          if (res.results) setResults(byId(res.results));
          setMessages([...history, ...notes, { role: "assistant", content: res.reply }]);
        })
        .catch(() => {
          toast.error("The assistant failed to respond");
          setMessages(messages);
        });
    },
    [parseTestCases, messages, assistant, dataSourceId, mappingEntryId, value, toast]
  );

  const onSubmit = useCallback(async () => {
    return update
      .mutateAsync({ id: mappingEntryId, updates: { transform: value } })
      .then(async () => {
        await listMappings.refetch();
        toast.success("Transformation updated");
        onClose();
      })
      .catch(() => {
        toast.error("Failed to update transformation");
      });
  }, [value, toast, update, mappingEntryId, onClose, listMappings]);

  const onRemove = useCallback(async () => {
    return update
      .mutateAsync({ id: mappingEntryId, updates: { transform: null } })
      .then(async () => {
        await listMappings.refetch();
        toast.success("Transformation removed");
        onClose();
      })
      .catch(() => {
        toast.error("Failed to remove transformation");
      });
  }, [listMappings, mappingEntryId, onClose, toast, update]);

  const working = assistant.isLoading;

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      title="Edit transformation"
      maxWidth={aiEnabled ? 1600 : 1200}
    >
      <Stack vertical gap="large">
        <div style={{ height: "65vh" }}>
          <Allotment separator={false}>
            {aiEnabled && (
              <Allotment.Pane preferredSize="30%">
                <ChatPane messages={messages} onSend={onSendMessage} loading={working} />
              </Allotment.Pane>
            )}
            <Allotment.Pane>
              <Stack
                vertical
                gap="xSmall"
                style={{ padding: "var(--spacing-small)", boxSizing: "border-box", height: "100%" }}
              >
                <Title>Code</Title>
                <CodeEditor value={value} onChange={setValue} height="100%" readOnly={working} />
              </Stack>
            </Allotment.Pane>
            <Allotment.Pane>
              <TestCasesPane
                format={format}
                testCases={testCases}
                onChange={setTestCases}
                results={results}
                onRun={onRunTests}
                disabled={working}
              />
            </Allotment.Pane>
          </Allotment>
        </div>
        <FormFooter
          left={
            <Button variant="outlined" onClick={() => setRemove(true)} disabled={working}>
              Remove
            </Button>
          }
          onCancel={onClose}
          onSubmit={onSubmit}
        />
      </Stack>
      <ConfirmationModal
        show={remove}
        onCancel={() => setRemove(false)}
        onConfirm={onRemove}
        config={{
          title: "Remove transformation",
          description: "Are you sure you want to remove the transformation?",
          confirmTitle: "Remove",
          cancelTitle: "Cancel"
        }}
      />
    </ModalV2>
  );
};
